import type { FC } from 'react';
import React, { useCallback, useContext, useState } from 'react';
import type { StripeElements, Stripe } from '@stripe/stripe-js';

import type { CreateNewPaymentRequest } from '../../services/stripe/types';
import noop from '../../utils/function/noop';
import { AppContext } from '../contextStore/AppContext';
import {
  getAgent,
  getCardCategories,
} from '../../utils/session/selectors';
import { NotificationContext } from '../contextStore/NotificationContext';
import useSessionExpirationReminder from '../../hooks/useSessionExpirationReminder';
import { GENERIC_STRIPE_ERROR_MESSAGE } from '../../shared/strings';
import StripePaymentElementWrapper from '../../components/stripe/stripe-elements-wrapper/StripePaymentElementWrapper';
import type { ApiError } from '../checkout-v2/types';
import type {
  PaymentMethodStatus,
  SetupPaymentMethodResponse,
} from '../../services/customer/types';
import usePollingIntervalController from '../../hooks/usePollingIntervalController/usePollingIntervalController';
import { getMedicationOptionsFromManufacturerCardTypeConfig } from '../../utils/session/getMedicationOptionsFromManufacturerCardTypeConfig';
import createCardCategoriesArray from '../../utils/cardCategories/createCardCategoriesArray';
import type { MedicationOption } from '../../shared/types/CardCategories/MedicationOption';
import type { CardCategoriesType } from '../../shared/types/CardCategories/CardCategoriesType';

import type { AddPaymentMethodBaseProps } from './types/AddPaymentMethodBaseProps';
import type { AddPaymentMethodFormSubmitData } from './components/AddPaymentMethodStripeForm';
import AddPaymentMethodFormStripe from './components/AddPaymentMethodStripeForm';
import { createPaymentMethodRequest } from './utils/createPaymentMethodRequest';

export type AddPaymentMethodContainerProps =
  AddPaymentMethodBaseProps & {
    formTitle: string;
    onError?: (error: ApiError) => void;
  };

const AddPaymentMethodContainer: FC<
  AddPaymentMethodContainerProps
> = ({
  stripeApi,
  customerApi,
  customerId,
  vendorPlatformKey,
  formTitle,
  isInFocus,
  onBeforeSubmit = noop,
  onSuccess = noop,
  onCancel = noop,
  onBackClick,
  onLoadComplete = noop,
  onError = noop,
}) => {
  const { setData, originalCheckoutSessionResponse } =
    useContext(AppContext);
  const { notify } = useContext(NotificationContext);
  const [
    isCreatingNewPaymentMethod,
    setIsCreatingNewPaymentMethod,
  ] = useState(false);
  const { initialSessionData } = useContext(AppContext);

  const medicationOptions: MedicationOption[] =
    getMedicationOptionsFromManufacturerCardTypeConfig(
      initialSessionData,
    );

  const cardCategoriesConfig = getCardCategories(
    initialSessionData,
  );

  const { setIsComponentUnmounting, setIsSubmitDone } =
    usePollingIntervalController({ customerId });

  const agent = getAgent(originalCheckoutSessionResponse);

  const handleError = (err: string) => {
    setIsCreatingNewPaymentMethod(false);
    setData({ overlayLoaderConfig: { show: false } });
    console.error(err);
  };

  const handleStripeError = (error: string) => {
    // `error` should not be displayed to the user as it's coming from Stripe.
    notify({
      severity: 'error',
      title: GENERIC_STRIPE_ERROR_MESSAGE,
    });
    handleError(error);
  };

  const isValidCard = useCallback(
    async (elements: StripeElements) => {
      const { validationError } =
        await stripeApi.validatePaymentMethod({ elements });

      if (validationError) {
        // Stripe handles showing errors in their UI so we don't need to do anything else.
        // We also don't start polling as the Stripe form is not valid.
        handleError(validationError.message);
        return false;
      }
      return true;
    },
    [stripeApi, handleError],
  );

  const tokenizeCard = async ({
    elements,
    stripe,
    name,
  }: Pick<
    CreateNewPaymentRequest,
    'elements' | 'stripe' | 'name'
  >) => {
    const {
      paymentMethod: tokenizedCard,
      paymentMethodError: tokenizedCardError,
    } = await stripeApi.createPaymentMethod({
      elements,
      stripe,
      name,
    });

    if (tokenizedCardError) {
      handleStripeError(tokenizedCardError.message);
      return undefined;
    }

    if (!tokenizedCard) {
      throw new Error('Error tokenizing card');
    }

    return tokenizedCard;
  };

  const notifyPaymentMethodWarning = (
    setupIntentResponse?: SetupPaymentMethodResponse,
  ) => {
    if (setupIntentResponse?.data.warning) {
      setData({
        addPaymentMethodWarning:
          setupIntentResponse?.data.warning,
      });
    }
  };

  const isAuthRequired = (status: PaymentMethodStatus) => {
    return status === 'AUTH_REQUIRED';
  };

  const authorizeCard = async ({
    stripe,
    setupPaymentMethodResponse,
    setupPaymentMethodUrl,
  }: {
    stripe: Stripe;
    setupPaymentMethodResponse: SetupPaymentMethodResponse;
    setupPaymentMethodUrl: string;
  }) => {
    const { status, vendorSetupPaymentMethodSecret } =
      setupPaymentMethodResponse.data;

    if (!isAuthRequired(status)) {
      return setupPaymentMethodResponse;
    }

    if (!vendorSetupPaymentMethodSecret) {
      throw new Error(
        'Failed to start authorization due to missing vendorSetupPaymentMethodSecret',
      );
    }

    /**
     * This launches Stripe's authorization screen, when adding a card
     * - stripe.handleNextAction resolves to a confirmed success/failure
     * for the given setupIntent, without the need to confirm it via the API
     * - UI however, relies upon CCG API instead of the resolved value from the promise
     * - Note: Card authorization was introduced starting v2.9.0
     */
    await stripe.handleNextAction({
      clientSecret: vendorSetupPaymentMethodSecret,
    });

    return customerApi.confirmPaymentMethodCreation({
      url: setupPaymentMethodUrl,
      isPaymentMethodAuthorized: true,
    });
  };

  const createPaymentMethod = async ({
    elements,
    stripe,
    name,
    nickname,
    isDefault,
    isManufacturerCard,
    cardCategories,
  }: CreateNewPaymentRequest & {
    isDefault: boolean;
    nickname: string;
    isManufacturerCard: boolean;
    cardCategories: CardCategoriesType;
  }) => {
    try {
      if (!(await isValidCard(elements))) {
        return;
      }

      const tokenizedCard = await tokenizeCard({
        elements,
        stripe,
        name,
      });

      if (!tokenizedCard) {
        return;
      }

      const createPaymentMethodRequestPayload =
        createPaymentMethodRequest(
          tokenizedCard.id,
          isDefault,
          nickname,
          name,
          isManufacturerCard,
          agent,
          cardCategories,
        );

      let createPaymentMethodResponse =
        await customerApi.createPaymentMethod(
          customerId,
          createPaymentMethodRequestPayload,
        );

      if (
        !createPaymentMethodResponse ||
        createPaymentMethodResponse?.errors?.length
      ) {
        throw new Error('Failed to add payment method');
      }

      createPaymentMethodResponse = await authorizeCard({
        stripe,
        setupPaymentMethodResponse: createPaymentMethodResponse,
        setupPaymentMethodUrl: createPaymentMethodResponse.url,
      });

      if (
        createPaymentMethodResponse?.errors?.length ||
        !createPaymentMethodResponse?.data?.paymentMethodId
      ) {
        throw new Error('Failed to add payment method');
      }

      const { paymentMethodId } =
        createPaymentMethodResponse.data;

      setIsCreatingNewPaymentMethod(false);
      onSuccess({
        paymentMethodId,
        showAddPaymentMethodNotificationAfterNav:
          !createPaymentMethodResponse?.data.warning,
      });
      notifyPaymentMethodWarning(createPaymentMethodResponse);
    } catch (e: any) {
      onError(e?.response?.data as ApiError);
      if (e instanceof Error) {
        handleError(e.message);
      }
    } finally {
      setData({ overlayLoaderConfig: { show: false } });
    }
  };

  const onSubmit = ({
    elements,
    formData: {
      nameOnCard: name,
      nickname,
      isDefault,
      isManufacturerCard,
      medication,
    },
    stripe,
  }: AddPaymentMethodFormSubmitData) => {
    setIsCreatingNewPaymentMethod(true);
    setData({ overlayLoaderConfig: { show: true } });
    onBeforeSubmit();

    createPaymentMethod({
      elements,
      stripe,
      name,
      nickname,
      isDefault,
      isManufacturerCard,
      cardCategories: createCardCategoriesArray({
        manufacturerCardTypeOptions: {
          value: medication,
          medications: medicationOptions,
        },
      }),
    })
      .catch(() => {
        // errors are already caught and handled inside createPaymentMethod itself
      })
      .finally(() => {
        setIsComponentUnmounting(false);
        setIsSubmitDone(true);
      });
  };

  useSessionExpirationReminder({
    notify,
    originalCheckoutSessionResponse,
  });

  return (
    <StripePaymentElementWrapper
      vendorPlatformKey={vendorPlatformKey}
    >
      <AddPaymentMethodFormStripe
        onSubmit={onSubmit}
        onBackClick={onBackClick}
        onCancel={onCancel}
        onLoadComplete={onLoadComplete}
        isCreatingNewPaymentMethod={isCreatingNewPaymentMethod}
        setIsCreatingNewPaymentMethod={
          setIsCreatingNewPaymentMethod
        }
        formTitle={formTitle}
        isInFocus={isInFocus}
        agent={agent}
        medicationOptions={medicationOptions}
        cardCategoriesConfigObject={cardCategoriesConfig}
        stripeApi={stripeApi}
      />
    </StripePaymentElementWrapper>
  );
};

export default AddPaymentMethodContainer;
